import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Check, X, Clock, Percent, Tag } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import Title from "@/components/Title";
import useUser from "@/lib/queries/user/useUser";
import usePricing from "@/lib/queries/usePricing";
import { useStytchSession } from "@stytch/react";
import { useState, useEffect } from "react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

interface AnimatedPriceProps {
  price: string;
  discountPrice?: string | null;
  hasDiscount?: boolean;
  isLoading?: boolean;
}

function AnimatedPrice({
  price,
  discountPrice,
  hasDiscount,
  isLoading,
}: AnimatedPriceProps) {
  const [displayPrice, setDisplayPrice] = useState(price);
  const [displayDiscountPrice, setDisplayDiscountPrice] =
    useState(discountPrice);

  useEffect(() => {
    if (price !== displayPrice && !isLoading) {
      // Delay the price update to allow exit animation
      const timer = setTimeout(() => {
        setDisplayPrice(price);
        setDisplayDiscountPrice(discountPrice);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [price, discountPrice, displayPrice, isLoading]);

  if (isLoading) {
    return (
      <div className="w-32 h-12 mx-auto bg-gray-200 rounded animate-pulse" />
    );
  }

  return (
    <div className="relative flex flex-col items-center justify-center overflow-hidden">
      <motion.div key={`${displayPrice}-${displayDiscountPrice}`}>
        <div className="flex flex-col items-center">
          {hasDiscount && displayDiscountPrice ? (
            <>
              {/* Original price with strikethrough */}
              <div className="flex items-baseline line-through text-muted-foreground opacity-70">
                {displayPrice.split("").map((char, index) => (
                  <motion.span
                    key={`original-${displayPrice}-${index}`}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 0.7 }}
                    exit={{ y: -20, opacity: 0 }}
                    transition={{
                      type: "tween",
                      delay: index * 0.05,
                      duration: 0.2,
                      ease: "easeOut",
                    }}
                    className="inline-block text-2xl font-bold"
                  >
                    {char}
                  </motion.span>
                ))}
              </div>
              {/* Discount price */}
              <div className="flex items-baseline text-green-600">
                {displayDiscountPrice.split("").map((char, index) => (
                  <motion.span
                    key={`discount-${displayDiscountPrice}-${index}`}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -20, opacity: 0 }}
                    transition={{
                      type: "tween",
                      delay: index * 0.1,
                      duration: 0.2,
                      ease: "easeOut",
                    }}
                    className="inline-block text-4xl font-bold"
                  >
                    {char}
                  </motion.span>
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-baseline">
              {displayPrice.split("").map((char, index) => (
                <motion.span
                  key={`${displayPrice}-${index}`}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -20, opacity: 0 }}
                  transition={{
                    type: "tween",
                    delay: index * 0.1,
                    duration: 0.2,
                    ease: "easeOut",
                  }}
                  className="inline-block text-4xl font-bold"
                >
                  {char}
                </motion.span>
              ))}
            </div>
          )}
          <span className="sr-only">
            {hasDiscount && displayDiscountPrice
              ? `Original price ${displayPrice}, discounted to ${displayDiscountPrice}`
              : displayPrice}
          </span>
        </div>
      </motion.div>
    </div>
  );
}

interface DiscountInfoProps {
  discountName: string;
  discountPercent: number;
  duration: number;
  repeats: string;
  isYearly?: boolean;
}

function DiscountInfo({
  discountName,
  discountPercent,
  duration,
  repeats,
  isYearly = false,
}: DiscountInfoProps) {
  const getDurationText = () => {
    if (repeats === "repeating for repeats") {
      return `${duration} months`;
    }
    return duration === 1 ? "1 month" : `${duration} months`;
  };

  const getDiscountDescription = () => {
    if (repeats === "repeating for repeats") {
      return `${discountPercent}% off for ${getDurationText()}`;
    }

    // Use the card type as source of truth for discount messaging
    if (isYearly) {
      return `${discountPercent}% off your first year`;
    }

    // For monthly plans, show monthly discount
    return `${discountPercent}% off your first ${duration.toString()} months`;
  };

  return (
    <div className="p-3 mx-4 border border-green-200 rounded-lg bg-green-50">
      <div className="flex items-center gap-2 mb-1">
        <Percent className="w-4 h-4 text-green-600" weight="bold" />
        <span className="text-sm font-semibold text-green-700">
          {discountName}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Clock className="w-4 h-4 text-green-600" />
        <span className="text-xs text-green-600">
          {getDiscountDescription()}
        </span>
      </div>
    </div>
  );
}

interface ToolUsesMeterProps {
  remaining: number;
  max: number;
}

function ToolUsesMeter({ remaining, max }: ToolUsesMeterProps) {
  const { t } = useTranslation("pricing");
  const percentage = (remaining / max) * 100;
  const used = max - remaining;

  return (
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">{t("toolUses.label")}</span>
      </div>
      <div className="w-full h-2 bg-gray-200 rounded-full">
        <div
          className="h-2 transition-all duration-300 rounded-full bg-primary"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="text-xs text-center text-muted-foreground">
        {t("toolUses.used", { used, remaining })}
      </div>
    </div>
  );
}

function ToolUsesLoader() {
  const { t } = useTranslation("pricing");
  return (
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">{t("toolUses.label")}</span>
      </div>
      <div className="w-full h-2 bg-gray-200 rounded-full">
        <div
          className="h-2 bg-gray-300 rounded-full animate-pulse"
          style={{ width: "60%" }}
        />
      </div>
      <div className="text-xs text-center text-muted-foreground">
        {t("toolUses.loading")}
      </div>
    </div>
  );
}

interface PricingTier {
  name: string;
  price: string;
  period?: string;
  description: string;
  features: string[];
  limitations?: string[];
  ctaText: string;
  ctaLink: string;
  popular?: boolean;
  savings?: string;
  discountPrice?: string | null;
  hasDiscount?: boolean;
  discountPercentage?: number;
  discountInfo?: {
    name: string;
    code: string;
    percent: number;
    duration: number;
    repeats: string;
  };
}

function CurrencyLoader() {
  const { t } = useTranslation("pricing");
  return (
    <div className="flex items-center justify-center gap-2 mt-6">
      <span className="text-sm font-medium text-muted-foreground">
        {t("loading.currency")}
      </span>
      <div className="w-24 h-10 bg-gray-200 rounded-md animate-pulse" />
    </div>
  );
}

function PricingCardSkeleton({ index }: { index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        delay: index * 0.1,
        ease: "easeOut",
      }}
    >
      <Card className="relative flex flex-col h-full min-h-[446px] w-full max-w-[380px] mx-auto">
        <CardHeader className="pb-4 text-center">
          <div className="w-20 mx-auto mb-4 bg-gray-200 rounded h-7 animate-pulse" />
          <div className="mt-4 mb-2">
            <div className="h-12 mx-auto bg-gray-200 rounded w-28 animate-pulse" />
          </div>
          <div className="w-56 h-4 mx-auto mt-2 bg-gray-200 rounded animate-pulse" />
        </CardHeader>

        <CardContent className="flex-grow px-6">
          <div className="space-y-3">
            <div className="w-16 h-4 mb-3 bg-gray-200 rounded animate-pulse" />
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex items-start gap-3 mb-3">
                <div className="w-4 h-4 bg-gray-200 rounded animate-pulse mt-0.5 flex-shrink-0" />
                <div
                  className={`h-4 bg-gray-200 rounded animate-pulse ${
                    i === 0
                      ? "w-48"
                      : i === 1
                        ? "w-52"
                        : i === 2
                          ? "w-44"
                          : "w-56"
                  }`}
                />
              </div>
            ))}
            <div className="h-8" />
          </div>
        </CardContent>

        <CardFooter className="px-6 pb-6">
          <div className="w-full h-10 bg-gray-200 rounded-md animate-pulse" />
        </CardFooter>
      </Card>
    </motion.div>
  );
}

function PricingCard({
  tier,
  user,
  index,
  userLoading,
  pricingLoading,
  selectedCurrency,
}: {
  tier: PricingTier;
  user?: any;
  index: number;
  userLoading?: boolean;
  pricingLoading?: boolean;
  selectedCurrency: string;
}) {
  const { t } = useTranslation("pricing");
  const isTrialCard = tier.name === t("tiers.trial.name");
  const isTrialUser = user && !user.subscribed && user.trialActive;
  const showMeter = isTrialCard && isTrialUser;

  // Helper function to get currency symbol
  const getCurrencySymbol = (currency: string) => {
    const currencySymbols: Record<string, string> = {
      usd: "$",
      eur: "€",
      gbp: "£",
    };
    return currencySymbols[currency] || "$";
  };

  // Format the price with the selected currency for trial card
  const getDisplayPrice = () => {
    if (isTrialCard) {
      return `${getCurrencySymbol(selectedCurrency)}${tier.price}`;
    }
    return tier.price;
  };

  const getDisplayDiscountPrice = () => {
    if (isTrialCard) {
      return tier.discountPrice
        ? `${getCurrencySymbol(selectedCurrency)}${tier.discountPrice}`
        : null;
    }
    return tier.discountPrice;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        delay: index * 0.1,
        ease: "easeOut",
      }}
    >
      <Card
        className={cn(
          "relative flex flex-col h-full",
          tier.popular && "shadow-lg scale-105"
        )}
        style={
          tier.popular
            ? {
                border: "2px solid transparent",
                backgroundImage:
                  "linear-gradient(hsl(var(--card)), hsl(var(--card))), linear-gradient(to right, hsl(var(--blue-gradient)), hsl(var(--primary)), hsl(var(--primary)))",
                backgroundOrigin: "border-box",
                backgroundClip: "padding-box, border-box",
              }
            : {}
        }
      >
        {tier.popular && (
          <div className="absolute transform -translate-x-1/2 -top-3 left-1/2">
            <Badge
              className="text-white border-0"
              style={{
                backgroundImage:
                  "linear-gradient(to right, hsl(var(--blue-gradient)), hsl(var(--primary)), hsl(var(--primary)))",
              }}
            >
              {t("badges.popular")}
            </Badge>
          </div>
        )}

        {tier.savings && (
          <div className="absolute -top-2 -right-2">
            <Badge
              variant="secondary"
              className="text-green-800 bg-green-100 border-green-200"
            >
              {tier.savings}
            </Badge>
          </div>
        )}

        <CardHeader className="pb-4 text-center">
          <CardTitle className="text-2xl font-bold">{tier.name}</CardTitle>

          <div className="mt-4">
            <AnimatedPrice
              price={getDisplayPrice()}
              discountPrice={getDisplayDiscountPrice()}
              hasDiscount={tier.hasDiscount}
              isLoading={pricingLoading}
            />
            {tier.period && (
              <span className="text-lg text-muted-foreground">
                {tier.period}
              </span>
            )}
          </div>

          {/* Show discount info below the price */}
          {tier.hasDiscount && tier.discountInfo && (
            <div className="mt-3">
              <DiscountInfo
                discountName={tier.discountInfo.name}
                discountPercent={tier.discountInfo.percent}
                duration={tier.discountInfo.duration}
                repeats={tier.discountInfo.repeats}
                isYearly={tier.name === "Yearly"}
              />
            </div>
          )}

          <p className="mt-2 text-sm text-muted-foreground">
            {tier.description}
          </p>
        </CardHeader>

        <CardContent className="flex-grow">
          <div className="space-y-3">
            <h4 className="mb-2 text-sm font-semibold text-green-700">
              {t("sections.included")}
            </h4>
            {tier.features.map((feature, index) => (
              <div key={index} className="flex items-start gap-3">
                <Check
                  className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0"
                  weight="bold"
                />
                <span className="text-sm">{feature}</span>
              </div>
            ))}

            {showMeter ? (
              <div className="mt-4">
                {userLoading ? (
                  <ToolUsesLoader />
                ) : (
                  <ToolUsesMeter
                    remaining={user.creditsRemaining}
                    max={user.creditsMax}
                  />
                )}
              </div>
            ) : (
              tier.limitations &&
              tier.limitations.length > 0 && (
                <>
                  <h4 className="mt-4 mb-2 text-sm font-semibold text-muted-foreground">
                    {t("sections.limitations")}
                  </h4>
                  {tier.limitations.map((limitation, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <X
                        className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0"
                        weight="bold"
                      />
                      <span className="text-sm text-muted-foreground">
                        {limitation}
                      </span>
                    </div>
                  ))}
                </>
              )
            )}
          </div>
        </CardContent>

        <CardFooter>
          <Button
            asChild
            className={cn(
              "w-full",
              tier.popular && "bg-primary hover:bg-primary/90"
            )}
            variant={tier.popular ? "default" : "outline"}
          >
            <Link to={tier.ctaLink}>{tier.ctaText}</Link>
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
}

function Pricing() {
  const { t } = useTranslation("pricing");
  const stytchSession = useStytchSession();
  const { data: user, isLoading: userLoading } = useUser({
    enabled: stytchSession.session !== null,
  });

  const [selectedCurrency, setSelectedCurrency] = useState("gbp");
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromoCode, setAppliedPromoCode] = useState("");

  const { data: pricingData, isLoading: pricingLoading } = usePricing({
    coupon: appliedPromoCode || undefined,
  });

  const handleApplyPromoCode = () => {
    if (promoCode.trim() && promoCode.trim().toUpperCase() !== appliedPromoCode) {
      setAppliedPromoCode(promoCode.trim().toUpperCase());
    }
  };

  const handleRemovePromoCode = () => {
    setAppliedPromoCode("");
    setPromoCode("");
  };

  const handlePromoCodeKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleApplyPromoCode();
    }
  };

  const isSubscribedUser = user && user.subscribed;

  // Helper function to format price based on currency
  const formatPrice = (unitPrice: number, currency: string) => {
    const price = (unitPrice / 100).toFixed(2);
    const currencySymbols: Record<string, string> = {
      usd: "$",
      eur: "€",
      gbp: "£",
    };
    const symbol = currencySymbols[currency] || "$";
    return `${symbol}${price}`;
  };

  const getPrice = (period: "monthly" | "yearly") => {
    if (!pricingData || pricingLoading) return null; // Return null when loading

    const prices = pricingData[period];
    const priceData = prices.find((p) => p.currency === selectedCurrency);

    if (!priceData) return period === "monthly" ? "$9.99" : "$79.99";

    return formatPrice(priceData.unitPrice, priceData.currency);
  };

  const getDiscountPrice = (period: "monthly" | "yearly") => {
    if (!pricingData || pricingLoading) return null;

    const prices = pricingData[period];
    const priceData = prices.find((p) => p.currency === selectedCurrency);

    if (!priceData) return null;

    return formatPrice(priceData.discountPrice, priceData.currency);
  };

  const hasDiscount = (period: "monthly" | "yearly") => {
    if (!pricingData || pricingLoading) return false;

    const prices = pricingData[period];
    const priceData = prices.find((p) => p.currency === selectedCurrency);

    if (!priceData) return false;

    return priceData.unitPrice !== priceData.discountPrice;
  };

  const getDiscountPercentage = (period: "monthly" | "yearly") => {
    if (!pricingData || pricingLoading) return 0;

    const prices = pricingData[period];
    const priceData = prices.find((p) => p.currency === selectedCurrency);

    if (!priceData || priceData.unitPrice === priceData.discountPrice) return 0;

    return Math.round(
      ((priceData.unitPrice - priceData.discountPrice) / priceData.unitPrice) *
        100
    );
  };

  const getTranslatedPricingTiers = (): PricingTier[] => [
    {
      name: t("tiers.trial.name"),
      price: "0.00",
      description: t("tiers.trial.description"),
      features: [
        t("tiers.trial.features.researcher"),
        t("tiers.trial.features.communications"),
        t("tiers.trial.features.limited"),
      ],
      limitations: [
        t("tiers.trial.limitations.usage"),
        t("tiers.trial.limitations.pilot"),
      ],
      ctaText: t("tiers.trial.cta"),
      ctaLink: "/authenticate/signup",
    },
    {
      name: t("tiers.monthly.name"),
      price: getPrice("monthly") || "£7.99",
      period: t("tiers.monthly.period"),
      description: t("tiers.monthly.description"),
      features: [
        t("tiers.monthly.features.unlimited"),
        t("tiers.monthly.features.pilot"),
        t("tiers.monthly.features.support"),
        t("tiers.monthly.features.advanced"),
      ],
      ctaText: t("tiers.monthly.cta"),
      ctaLink: appliedPromoCode ? `/checkout?promo=${appliedPromoCode}` : "/checkout",
      popular: true,
      discountPrice: getDiscountPrice("monthly"),
      hasDiscount: hasDiscount("monthly"),
      discountPercentage: getDiscountPercentage("monthly"),
      discountInfo: pricingData?.discount,
    },
    {
      name: t("tiers.yearly.name"),
      price: getPrice("yearly") || "£89.99",
      period: t("tiers.yearly.period"),
      description: t("tiers.yearly.description"),
      features: [
        t("tiers.yearly.features.unlimited"),
        t("tiers.yearly.features.pilot"),
        t("tiers.yearly.features.support"),
        t("tiers.yearly.features.advanced"),
      ],
      ctaText: t("tiers.yearly.cta"),
      ctaLink: appliedPromoCode ? `/checkout?yearly=true&promo=${appliedPromoCode}` : "/checkout?yearly=true",
      savings: t("tiers.yearly.savings"),
      discountPrice: getDiscountPrice("yearly"),
      hasDiscount: hasDiscount("yearly"),
      discountPercentage: getDiscountPercentage("yearly"),
      discountInfo: pricingData?.discount,
    },
  ];

  const dynamicPricingTiers = getTranslatedPricingTiers();

  const availableCurrencies = pricingData?.monthly.map((p) => p.currency) || [
    "gbp",
  ];

  return (
    <main className="flex flex-col w-full min-h-screen px-10 py-8 overflow-auto md:px-20 md:py-12 bg-very-light">
      <div className="max-w-6xl mx-auto">
        <div className="mb-12 text-center">
          <Title title={t("title")}>{t("title")}</Title>
          <p className="max-w-2xl mx-auto mt-4 text-lg text-muted-foreground">
            {t("description")}
          </p>

          {/* Currency Selection and Promo Code */}
          {pricingLoading ? (
            <CurrencyLoader />
          ) : (
            <div className="flex flex-col items-center justify-between w-full gap-4 mt-6 sm:flex-row">
              {/* Currency Selector */}
              {pricingData && availableCurrencies.length > 1 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-muted-foreground">
                    {t("currency.label")}
                  </span>
                  <Select
                    value={selectedCurrency}
                    onValueChange={setSelectedCurrency}
                  >
                    <SelectTrigger className="flex flex-row items-center border w-fit border-muted">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableCurrencies.map((currency) => (
                        <SelectItem key={currency} value={currency}>
                          <div className="flex flex-row items-center gap-2 pr-2">
                            <span>{currency.toUpperCase()}</span>
                            <span className="">
                              (
                              {currency === "usd"
                                ? "$"
                                : currency === "eur"
                                  ? "€"
                                  : currency === "gbp"
                                    ? "£"
                                    : ""}
                              )
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Promo Code Input */}
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder={t("promo.placeholder")}
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  onKeyDown={handlePromoCodeKeyPress}
                  className="w-40 uppercase"
                />
                {appliedPromoCode && promoCode.toUpperCase() === appliedPromoCode ? (
                  <Button
                    onClick={handleRemovePromoCode}
                    variant="ghost"
                    size="sm"
                  >
                    {t("promo.remove")}
                  </Button>
                ) : (
                  <Button
                    onClick={handleApplyPromoCode}
                    disabled={!promoCode.trim()}
                    variant="outline"
                    size="sm"
                  >
                    {t("promo.apply")}
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Subscription Status for Subscribed Users */}
          {isSubscribedUser && (
            <div className="max-w-md p-4 mx-auto mt-6 border border-green-200 rounded-lg bg-green-50">
              <div className="flex items-center justify-center gap-2">
                <span className="font-medium text-green-700">
                  {t("subscription.active.title")}
                </span>
              </div>
              <p className="mt-1 text-sm text-green-600">
                {t("subscription.active.description")}
              </p>
            </div>
          )}
        </div>

        <div className="grid gap-8 mb-12 md:grid-cols-3">
          {pricingLoading
            ? [...Array(3)].map((_, index) => (
                <PricingCardSkeleton key={index} index={index} />
              ))
            : dynamicPricingTiers.map((tier, index) => (
                <PricingCard
                  key={tier.name}
                  tier={tier}
                  user={user}
                  index={index}
                  userLoading={userLoading}
                  pricingLoading={pricingLoading}
                  selectedCurrency={selectedCurrency}
                />
              ))}
        </div>

        {/* TODO: waiting for support & faq pages from gizmo */}
        {/* <div className="space-y-6 text-center">
          <div className="p-6 bg-white border rounded-lg shadow-sm">
            <h3 className="mb-4 text-xl font-semibold">
              Questions about pricing?
            </h3>
            <p className="mb-4 text-muted-foreground">
              We're here to help you choose the right plan for your teaching
              needs.
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <Button variant="outline" asChild>
                <Link to="mailto:support@tchr.tools">Contact Support</Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to={FAQ}>View FAQ</Link>
              </Button>
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            All plans include secure data handling and regular updates. Cancel
            anytime.
          </p>
        </div> */}
      </div>
    </main>
  );
}

export default Pricing;
